import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError, AccountInfo } from '@azure/msal-browser';
import { loginRequest } from '../authConfig';
import { setAuthToken } from '../services/api';
import { useTeams } from './TeamsContext';
import { app, authentication } from '@microsoft/teams-js';
import { ENV } from '../utils/env';

const APPLICATION_ID_URI = "api://chatvnt2.vontierdatadev.com/0a41dae4-9282-486f-8e39-226602f817ef";

// Define custom user type with roles
interface ExtendedUser extends AccountInfo {
  roles: string[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: ExtendedUser | null;
  login: () => void;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const { isInTeams, getTeamsAuthToken } = useTeams();
  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!accounts[0]);

  // Extract roles from ID token claims and update user
  const updateUserWithRoles = async (account: AccountInfo) => {
    try {
      // Extract user info from account
      const extendedUser: ExtendedUser = {
        ...account,
        roles: [] // Default empty roles array
      };

      // Try to get ID token claims which may contain roles
      if (instance) {
        try {
          const currentAccount = instance.getActiveAccount();
          if (currentAccount) {
            // Get ID token claims which may contain roles
            const idTokenClaims = currentAccount.idTokenClaims as any;

            // Check for roles in various possible formats
            const roles = idTokenClaims?.roles ||
              idTokenClaims?.['roles'] ||
              idTokenClaims?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
              [];

            // Ensure roles is always an array
            extendedUser.roles = Array.isArray(roles) ? roles : [roles];
          }
        } catch (error) {
          console.error('Error getting token claims:', error);
        }
      }

      // For demo purposes, if Azure AD roles aren't configured, use TheChat as a default role
      // REMOVE THIS FOR PRODUCTION - Just for easier testing
      if (extendedUser.roles.length === 0) {
        extendedUser.roles = ['TheChat'];
      }

      setUser(extendedUser);
      setIsAuthenticated(true);

      return extendedUser;
    } catch (error) {
      console.error('Error updating user with roles:', error);
      return null;
    }
  };

  useEffect(() => {
    if (accounts[0]) {
      updateUserWithRoles(accounts[0]);
      setIsAuthenticated(true);
    } else {
      setUser(null);
      setIsAuthenticated(false);
    }
  }, [accounts]);

  // Check for active accounts on initial load
  useEffect(() => {
    const activeAccount = instance.getActiveAccount();
    if (activeAccount) {
      updateUserWithRoles(activeAccount);
      setIsAuthenticated(true);
    }
  }, []);

  const login = async () => {
    try {
      // If in Teams, use Teams SSO. If not in Teams, use MSAL popup.
      if (isInTeams) {
        try {
          const ssoToken = await getTeamsAuthToken();

          if (ssoToken) {
            setAuthToken(ssoToken);

            const context = await app.getContext();
            const teamsUser: ExtendedUser = {
              homeAccountId: context.user?.id || 'teams-user',
              environment: '',
              tenantId: context.user?.tenant?.id || ENV.TENANT_ID,
              username: context.user?.loginHint || context.user?.userPrincipalName || 'teams@user.com',
              localAccountId: context.user?.id || 'teams-user',
              name: context.user?.displayName || 'Teams User',
              roles: ['TheChat']
            };

            setUser(teamsUser);
            setIsAuthenticated(true);
            return;
          } else {
            throw new Error('Teams SSO failed to return a token. This usually means you need to consent to the app permissions or the app is not properly configured in Azure AD.');
          }
        } catch (teamsError) {
          console.error('Teams SSO error:', teamsError instanceof Error ? teamsError.message : teamsError);
          throw new Error(`Teams authentication failed: ${teamsError instanceof Error ? teamsError.message : 'Unknown error'}. Please try accessing the app directly in a web browser instead.`);
        }
      }

      // If not in Teams, use standard MSAL popup authentication
      try {
        const response = await instance.loginPopup({
          ...loginRequest,
          prompt: 'select_account',
          popupWindowAttributes: {
            popupSize: { width: 600, height: 600 },
            popupPosition: { top: 100, left: 100 }
          }
        });

        if (response && response.account) {
          instance.setActiveAccount(response.account);
        }
      } catch (popupError) {
        console.error('MSAL popup error:', popupError instanceof Error ? popupError.message : 'Unknown popup error');
        throw popupError;
      }
    } catch (error) {
      console.error('Login error:', error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  };

  const logout = () => {
    instance.logoutRedirect();
  };

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!user) return null;

    // If we're in Teams, try to get Teams token first
    if (isInTeams) {
      try {
        const teamsToken = await getTeamsAuthToken();
        if (teamsToken) {
          setAuthToken(teamsToken);
          return teamsToken;
        }
      } catch (error) {
        console.error('Error getting Teams token:', error);
      }
    }

    // Fall back to regular MSAL token acquisition
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: user
      });
      const accessToken = tokenResponse.accessToken;

      // Update user roles from new token
      const currentAccount = instance.getActiveAccount();
      if (currentAccount) {
        updateUserWithRoles(currentAccount);
      }

      setAuthToken(accessToken);
      return accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // In Teams iframe, we cannot use redirects
        if (isInTeams) {
          console.error('Token refresh requires interaction but redirects not supported in Teams iframe');
          return null;
        }

        try {
          // acquireTokenRedirect returns void, so we need to handle this differently
          instance.acquireTokenRedirect(loginRequest);
          // We won't reach here because of redirect, but return null to satisfy TypeScript
          return null;
        } catch (redirectError) {
          console.error('Error during token redirect:', redirectError);
          return null;
        }
      }
      console.error('Error acquiring token:', error);
      return null;
    }
  }, [user, instance, isInTeams, getTeamsAuthToken]);

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    getAccessToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};